/**
 * Per `embed-fan-gating` capability — Fan Cognito PKCE flow for embed.
 *
 * Mirrors admin-ui's auth.ts but targets the SECOND Cognito pool
 * (Fan pool, separate issuer + client id). Token stash is sessionStorage
 * (not localStorage) — Decision 5 in design: tabs reset cleanly,
 * keeps shared-device hygiene; Cognito's own session cookie makes the
 * "log in again" tap feel instant via SSO.
 *
 * Env vars (Vite-baked at build time):
 *   VITE_FAN_COGNITO_HOSTED_UI_BASE_URL  https://<prefix>.auth.<region>.amazoncognito.com
 *   VITE_FAN_COGNITO_CLIENT_ID           Fan App Client id
 *   VITE_FAN_COGNITO_REDIRECT_URI        absolute callback URL (e.g. https://embeds.../v2/<slug>/)
 *   VITE_FAN_COGNITO_SIGN_OUT_URI        absolute logout target
 *   VITE_FAN_COGNITO_SCOPES              space-separated, default 'openid email profile'
 */

const ID_TOKEN_KEY = 'embedFanIdToken';
const ACCESS_TOKEN_KEY = 'embedFanAccessToken';
const REFRESH_TOKEN_KEY = 'embedFanRefreshToken';
const ID_TOKEN_EXP_KEY = 'embedFanIdTokenExp';
const PKCE_VERIFIER_KEY = 'embedFanPkceVerifier';
const PKCE_STATE_KEY = 'embedFanPkceState';

export interface FanAuthState {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  claims: {
    sub: string;
    email: string;
    name?: string;
    exp: number;
  };
}

interface FanCognitoConfig {
  hostedUiBaseUrl: string;
  clientId: string;
  redirectUri: string;
  signOutRedirectUri: string;
  scopes: string;
  /** User Pool id (e.g. ap-northeast-1_xxx). Required for the SRP
   *  custom-login path; optional for Hosted-UI-only builds. */
  userPoolId?: string;
}

function readEnv(name: string): string | undefined {
  const viteEnv = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  const fromVite = viteEnv?.[name];
  if (fromVite) return fromVite;
  const procEnv = (typeof process !== 'undefined' && process.env) ? process.env : null;
  return procEnv?.[name] || undefined;
}

function getConfig(): FanCognitoConfig | null {
  const hostedUiBaseUrl = readEnv('VITE_FAN_COGNITO_HOSTED_UI_BASE_URL');
  const clientId = readEnv('VITE_FAN_COGNITO_CLIENT_ID');
  const redirectUri = readEnv('VITE_FAN_COGNITO_REDIRECT_URI');
  if (!hostedUiBaseUrl || !clientId || !redirectUri) return null;
  return {
    hostedUiBaseUrl: hostedUiBaseUrl.replace(/\/+$/, ''),
    clientId,
    redirectUri,
    signOutRedirectUri:
      readEnv('VITE_FAN_COGNITO_SIGN_OUT_URI') ?? redirectUri,
    scopes: readEnv('VITE_FAN_COGNITO_SCOPES') ?? 'openid email profile',
    userPoolId: readEnv('VITE_FAN_COGNITO_USER_POOL_ID'),
  };
}

/** True when the build is configured for fan auth. Drives the
 *  "show login button" gate in EmbedRuntime. */
export function fanCognitoConfigured(): boolean {
  return getConfig() !== null;
}

// ── PKCE helpers ────────────────────────────────────────────────
function base64UrlEncode(bytes: Uint8Array): string {
  let str = '';
  for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]!);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(byteLen = 32): string {
  const buf = new Uint8Array(byteLen);
  crypto.getRandomValues(buf);
  return base64UrlEncode(buf);
}

async function sha256B64Url(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

/** Redirects the browser to the Hosted UI sign-in page. Never returns.
 *  When `identityProvider` is passed (e.g. 'Google'), Cognito skips the
 *  Hosted UI landing page and goes straight to the federated provider's
 *  consent screen — the "用 Google 登入" affordance. */
export async function signInAsFan(opts: { identityProvider?: 'Google' } = {}): Promise<void> {
  const cfg = getConfig();
  if (!cfg) throw new Error('Fan Cognito not configured (missing VITE_FAN_COGNITO_* env vars)');
  const verifier = randomString(48);
  const challenge = await sha256B64Url(verifier);
  const state = randomString(16);
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(PKCE_STATE_KEY, state);
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: cfg.clientId,
    redirect_uri: cfg.redirectUri,
    scope: cfg.scopes,
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  if (opts.identityProvider) params.set('identity_provider', opts.identityProvider);
  window.location.assign(`${cfg.hostedUiBaseUrl}/oauth2/authorize?${params.toString()}`);
}

/** Sign out — clears local stash + redirects to Hosted UI logout
 *  endpoint so the Cognito-side cookie is invalidated. */
export function signOutFan(): void {
  clearStoredFanTokens();
  const cfg = getConfig();
  if (!cfg) {
    window.location.assign('/');
    return;
  }
  const params = new URLSearchParams({
    client_id: cfg.clientId,
    logout_uri: cfg.signOutRedirectUri,
  });
  window.location.assign(`${cfg.hostedUiBaseUrl}/logout?${params.toString()}`);
}

/** Exchange ?code= for tokens. Caller is the embed runtime when it
 *  detects an auth callback URL (typically by reading window.location
 *  on mount). Returns the auth state on success; throws on any
 *  failure (state mismatch / expired code / network). */
export async function handleFanAuthCallback(href: string = window.location.href): Promise<FanAuthState> {
  const cfg = getConfig();
  if (!cfg) throw new Error('Fan Cognito not configured');
  const url = new URL(href);
  const code = url.searchParams.get('code');
  const state = url.searchParams.get('state');
  // Cognito returns ?error=... when the upstream IdP (Google) rejected
  // the request OR when the App Client config is wrong. Surface the
  // exact reason so debugging doesn't need server logs.
  const errParam = url.searchParams.get('error');
  const errDesc = url.searchParams.get('error_description');
  if (errParam) {
    throw new Error(`Cognito returned error: ${errParam}${errDesc ? ` — ${decodeURIComponent(errDesc)}` : ''}`);
  }
  if (!code) {
    const seen = Array.from(url.searchParams.keys()).join(', ') || '(none)';
    throw new Error(`missing ?code in callback URL (saw params: ${seen})`);
  }

  const expectedState = sessionStorage.getItem(PKCE_STATE_KEY);
  if (!expectedState || expectedState !== state) {
    sessionStorage.removeItem(PKCE_STATE_KEY);
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    throw new Error('OAuth state mismatch — possible CSRF, refusing to exchange code');
  }
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  if (!verifier) throw new Error('PKCE verifier missing — sign-in did not start in this tab');

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: cfg.clientId,
    code,
    redirect_uri: cfg.redirectUri,
    code_verifier: verifier,
  });
  const res = await fetch(`${cfg.hostedUiBaseUrl}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`token exchange failed: ${res.status} ${text}`);
  }
  const tokens = (await res.json()) as {
    id_token: string;
    access_token: string;
    refresh_token: string;
    expires_in: number;
  };
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(PKCE_STATE_KEY);
  return saveTokensAndDecode(tokens);
}

function decodeJwt(jwt: string): Record<string, unknown> {
  const parts = jwt.split('.');
  if (parts.length !== 3) throw new Error('not a JWT');
  const segment = parts[1]!;
  const padded = segment
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(segment.length + ((4 - (segment.length % 4)) % 4), '=');
  const json = atob(padded);
  return JSON.parse(decodeURIComponent(escape(json))) as Record<string, unknown>;
}

function saveTokensAndDecode(t: {
  id_token: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
}): FanAuthState {
  sessionStorage.setItem(ID_TOKEN_KEY, t.id_token);
  sessionStorage.setItem(ACCESS_TOKEN_KEY, t.access_token);
  sessionStorage.setItem(REFRESH_TOKEN_KEY, t.refresh_token);
  const expiresAt = Date.now() + Math.max(0, t.expires_in - 30) * 1000;
  sessionStorage.setItem(ID_TOKEN_EXP_KEY, String(expiresAt));
  const claims = decodeJwt(t.id_token);
  return {
    idToken: t.id_token,
    accessToken: t.access_token,
    refreshToken: t.refresh_token,
    claims: {
      sub: typeof claims.sub === 'string' ? claims.sub : '',
      email: typeof claims.email === 'string' ? claims.email : '',
      name: typeof claims.name === 'string' ? claims.name : undefined,
      exp: typeof claims.exp === 'number' ? claims.exp : 0,
    },
  };
}

/** Synchronous read — returns the stashed id-token or null. */
export function getStoredFanIdToken(): string | null {
  return sessionStorage.getItem(ID_TOKEN_KEY);
}

/** Decoded claims from the stash, or null when no token / unparsable. */
export function getStoredFanClaims(): FanAuthState['claims'] | null {
  const idToken = sessionStorage.getItem(ID_TOKEN_KEY);
  if (!idToken) return null;
  try {
    const claims = decodeJwt(idToken);
    return {
      sub: typeof claims.sub === 'string' ? claims.sub : '',
      email: typeof claims.email === 'string' ? claims.email : '',
      name: typeof claims.name === 'string' ? claims.name : undefined,
      exp: typeof claims.exp === 'number' ? claims.exp : 0,
    };
  } catch {
    return null;
  }
}

export function clearStoredFanTokens(): void {
  sessionStorage.removeItem(ID_TOKEN_KEY);
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(ID_TOKEN_EXP_KEY);
  // Reset per-session flags so next login retriggers them.
  sessionStorage.removeItem('nx_yt_refreshed');
}

// ── Direct SRP sign-in (custom Login form) ─────────────────────
// Lazy-load amazon-cognito-identity-js so the SDK only ships when
// the user actually reaches the sign-in flow (~25KB gzipped).

type CognitoSdk = typeof import('amazon-cognito-identity-js');
let _cognitoSdk: Promise<CognitoSdk> | null = null;
function loadCognitoSdk(): Promise<CognitoSdk> {
  if (!_cognitoSdk) _cognitoSdk = import('amazon-cognito-identity-js');
  return _cognitoSdk;
}

function getUserPool(sdk: CognitoSdk) {
  const cfg = getConfig();
  if (!cfg) throw new Error('Fan Cognito is not configured');
  if (!cfg.userPoolId) {
    throw new Error('VITE_FAN_COGNITO_USER_POOL_ID is required for direct sign-in');
  }
  return new sdk.CognitoUserPool({
    UserPoolId: cfg.userPoolId,
    ClientId: cfg.clientId,
  });
}

/** Friendly error wrapper — `code` is the Cognito __type (e.g.
 *  "NotAuthorizedException"), `message` the human string. */
export class FanCognitoApiError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
    this.name = 'FanCognitoApiError';
  }
}

function toFanCognitoApiError(err: Error): FanCognitoApiError {
  const e = err as Error & { code?: string };
  return new FanCognitoApiError(e.code || e.name || 'UnknownError', e.message);
}

/** Thrown when Cognito demands a new password be set before issuing
 *  tokens (admin-created users, force-reset flow). The user instance
 *  carries the in-flight challenge so the caller can complete it
 *  without re-authenticating with the old password. */
export class FanNewPasswordRequiredError extends Error {
  readonly user: unknown;
  readonly requiredAttributes: string[];
  constructor(user: unknown, requiredAttributes: string[]) {
    super('NEW_PASSWORD_REQUIRED');
    this.name = 'FanNewPasswordRequiredError';
    this.user = user;
    this.requiredAttributes = requiredAttributes;
  }
}

/** Username + password sign-in via SRP. Stashes tokens in the same
 *  sessionStorage keys as the Hosted-UI flow so the rest of the SPA
 *  (fan-api request helper) doesn't need to know which path ran. */
export async function signInFanWithPassword(
  email: string,
  password: string,
): Promise<FanAuthState> {
  const sdk = await loadCognitoSdk();
  const pool = getUserPool(sdk);
  const user = new sdk.CognitoUser({ Username: email, Pool: pool });
  user.setAuthenticationFlowType('USER_SRP_AUTH');
  const authDetails = new sdk.AuthenticationDetails({
    Username: email,
    Password: password,
  });
  return new Promise<FanAuthState>((resolve, reject) => {
    user.authenticateUser(authDetails, {
      onSuccess: (session) => {
        const state = saveTokensAndDecode({
          id_token: session.getIdToken().getJwtToken(),
          access_token: session.getAccessToken().getJwtToken(),
          refresh_token: session.getRefreshToken().getToken(),
          expires_in: 3600,
        });
        resolve(state);
      },
      onFailure: (err) => reject(toFanCognitoApiError(err)),
      newPasswordRequired: (_attrs, requiredAttributes) => {
        reject(new FanNewPasswordRequiredError(user, requiredAttributes ?? []));
      },
    });
  });
}

/** Complete the NEW_PASSWORD_REQUIRED challenge after a Login form
 *  caught FanNewPasswordRequiredError and prompted for a new password. */
export async function completeFanNewPasswordChallenge(
  user: unknown,
  newPassword: string,
): Promise<FanAuthState> {
  const sdk = await loadCognitoSdk();
  return new Promise<FanAuthState>((resolve, reject) => {
    (user as InstanceType<CognitoSdk['CognitoUser']>).completeNewPasswordChallenge(
      newPassword,
      {},
      {
        onSuccess: (session) => {
          const state = saveTokensAndDecode({
            id_token: session.getIdToken().getJwtToken(),
            access_token: session.getAccessToken().getJwtToken(),
            refresh_token: session.getRefreshToken().getToken(),
            expires_in: 3600,
          });
          resolve(state);
        },
        onFailure: (err) => reject(toFanCognitoApiError(err)),
      },
    );
    void sdk;
  });
}

/** Self-service signup. After this resolves the user is UNCONFIRMED;
 *  Cognito mails a verification link that lands on /confirm (the
 *  existing email-link auto-confirm page). displayName, if given,
 *  stored as the OIDC `name` attribute. */
export async function signUpFan(
  email: string,
  password: string,
  displayName?: string,
): Promise<void> {
  const sdk = await loadCognitoSdk();
  const pool = getUserPool(sdk);
  const attrs: InstanceType<CognitoSdk['CognitoUserAttribute']>[] = [
    new sdk.CognitoUserAttribute({ Name: 'email', Value: email }),
  ];
  if (displayName && displayName.trim()) {
    attrs.push(new sdk.CognitoUserAttribute({ Name: 'name', Value: displayName.trim() }));
  }
  return new Promise<void>((resolve, reject) => {
    pool.signUp(email, password, attrs, [], (err) => {
      if (err) reject(toFanCognitoApiError(err));
      else resolve();
    });
  });
}

/** Trigger password reset — Cognito mails a 6-digit code. Pair with
 *  confirmForgotPasswordFan() to complete the flow. */
export async function forgotPasswordFan(email: string): Promise<void> {
  const sdk = await loadCognitoSdk();
  const pool = getUserPool(sdk);
  const user = new sdk.CognitoUser({ Username: email, Pool: pool });
  return new Promise<void>((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(toFanCognitoApiError(err)),
    });
  });
}

/** Set new password using the code from forgotPasswordFan(). */
export async function confirmForgotPasswordFan(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  const sdk = await loadCognitoSdk();
  const pool = getUserPool(sdk);
  const user = new sdk.CognitoUser({ Username: email, Pool: pool });
  return new Promise<void>((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(toFanCognitoApiError(err)),
    });
  });
}

/** Manual confirm — fan pastes 6-digit code into the /confirm-account
 *  page when the email link doesn't work (closed tab, link truncated). */
export async function confirmFanSignUp(
  email: string,
  code: string,
): Promise<void> {
  const sdk = await loadCognitoSdk();
  const pool = getUserPool(sdk);
  const user = new sdk.CognitoUser({ Username: email, Pool: pool });
  return new Promise<void>((resolve, reject) => {
    user.confirmRegistration(code, true, (err) => {
      if (err) reject(toFanCognitoApiError(err));
      else resolve();
    });
  });
}

/** Re-send the verification code for an UNCONFIRMED account. */
export async function resendFanConfirmationCode(email: string): Promise<void> {
  const sdk = await loadCognitoSdk();
  const pool = getUserPool(sdk);
  const user = new sdk.CognitoUser({ Username: email, Pool: pool });
  return new Promise<void>((resolve, reject) => {
    user.resendConfirmationCode((err) => {
      if (err) reject(toFanCognitoApiError(err));
      else resolve();
    });
  });
}

/** Best-effort silent refresh near expiry. Returns null if no
 *  refresh token / refresh fails. */
let inFlightRefresh: Promise<FanAuthState | null> | null = null;
export async function refreshFanTokenIfNeeded(windowMs = 5 * 60 * 1000): Promise<FanAuthState | null> {
  const expRaw = sessionStorage.getItem(ID_TOKEN_EXP_KEY);
  if (!expRaw) return null;
  const exp = Number(expRaw);
  if (Date.now() < exp - windowMs) return null;
  if (inFlightRefresh) return inFlightRefresh;
  inFlightRefresh = (async () => {
    const cfg = getConfig();
    if (!cfg) return null;
    const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) return null;
    try {
      const body = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: cfg.clientId,
        refresh_token: refreshToken,
      });
      const res = await fetch(`${cfg.hostedUiBaseUrl}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!res.ok) return null;
      const tokens = (await res.json()) as {
        id_token: string;
        access_token: string;
        expires_in: number;
        refresh_token?: string;
      };
      return saveTokensAndDecode({
        id_token: tokens.id_token,
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token ?? refreshToken,
        expires_in: tokens.expires_in,
      });
    } catch {
      return null;
    } finally {
      inFlightRefresh = null;
    }
  })();
  return inFlightRefresh;
}
